import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Users, HeartHandshake, Send, FileText, AlertTriangle, ChevronDown, FileDown, Share2, Save, FolderOpen, Trash2, Eye, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { exportComunicazione } from "../lib/exporter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const TEMPLATES = {
  ospiti: {
    subject: "Comunicazione importante: cambio di gestione",
    body: `Gentili familiari e ospiti,

desideriamo informarVi che, a partire da questo mese, la gestione della struttura non sarà più affidata a Zacastel S.r.l., ma passerà a Orto della Longevità S.r.l.

Vogliamo rassicurarVi: nulla cambierà nella cura, nell'attenzione e nella qualità dei servizi che ogni giorno dedichiamo ai Vostri cari. Lo stesso personale, con la stessa dedizione, continuerà a prendersi cura di loro.

Nei prossimi giorni Vi comunicheremo i nuovi riferimenti bancari (banca e IBAN) da utilizzare per i pagamenti.

Restiamo a Vostra completa disposizione per qualsiasi chiarimento.

Un caro saluto,
Orto della Longevità S.r.l.`,
  },
  operatori: {
    subject: "Comunicazione importante: cambio di gestione",
    body: `Care colleghe e cari colleghi,

Vi informiamo che, a partire da questo mese, la società che gestisce la struttura non sarà più Zacastel S.r.l. ma Orto della Longevità S.r.l.

Per Voi non cambia nulla: il Vostro lavoro, i turni e i punti di riferimento restano invariati. Vi comunicheremo a breve gli eventuali aggiornamenti amministrativi, inclusi i nuovi riferimenti bancari.

Grazie per l'impegno e la dedizione che dedicate ogni giorno ai nostri ospiti.

Un cordiale saluto,
Orto della Longevità S.r.l.`,
  },
  fornitori: {
    subject: "Comunicazione importante: cambio di gestione",
    body: `Spett.le Fornitore,

con la presente Vi informiamo che, a partire da questo mese, la gestione della struttura non sarà più affidata a Zacastel S.r.l., ma passerà a Orto della Longevità S.r.l.

Il rapporto di fornitura prosegue regolarmente e senza variazioni. Vi comunicheremo a breve gli eventuali aggiornamenti amministrativi, inclusi i nuovi riferimenti per la fatturazione e i pagamenti.

Per qualsiasi necessità restiamo a Vostra completa disposizione.

Cordiali saluti,
Orto della Longevità S.r.l.`,
  },
};

const wrapHtml = (subject, body, logoUrl = "", heading = "", footer = "") => {
  const paras = body.split(/\n{2,}/).map((p) => `<p style="margin:0 0 14px;line-height:1.6">${p.replace(/\n/g, "<br/>")}</p>`).join("");
  const logoImg = logoUrl ? `<img src="${logoUrl}" alt="Logo" style="display:block;height:44px;max-width:190px;object-fit:contain;background:#ffffff;border-radius:8px;padding:4px 6px;margin:0 0 12px"/>` : "";
  const headingHtml = heading ? `<div style="font-size:13px;font-weight:bold;letter-spacing:.5px;opacity:.95;margin:0 0 6px;text-transform:uppercase">${heading}</div>` : "";
  const footerHtml = footer ? `<div style="text-align:center;color:#6b7280;font-size:12px;margin-top:14px;line-height:1.6">${footer}</div>` : "";
  return `<div style="font-family:Arial,Helvetica,sans-serif;color:#1f2937;max-width:600px;margin:0 auto">
    <div style="background:#1f7a5c;color:#fff;padding:18px 22px;border-radius:12px 12px 0 0">
      ${logoImg}
      ${headingHtml}
      <div style="font-size:18px;font-weight:bold">${subject}</div>
    </div>
    <div style="padding:22px;border:1px solid #e5e7eb;border-top:none;border-radius:0 0 12px 12px;background:#fff">
      ${paras}
    </div>
    ${footerHtml}
  </div>`;
};

export default function EmailMassiva() {
  const { selectedStructure, user, companyLogo } = useAuth();
  const qc = useQueryClient();
  const [target, setTarget] = useState("ospiti");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showList, setShowList] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadedDraftId, setLoadedDraftId] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fromName, setFromName] = useState("");
  const [heading, setHeading] = useState("");
  const [footerLine, setFooterLine] = useState("");
  const [savingDefault, setSavingDefault] = useState(false);

  useQuery({
    queryKey: ["impostazioni-email-mittente"],
    queryFn: async () => {
      const d = (await api.get("/impostazioni/email")).data;
      setFromName((prev) => prev || d?.email_from_name || "");
      setHeading((prev) => prev || d?.email_heading || "");
      const parts = [d?.footer_indirizzo, d?.footer_telefono ? `Tel: ${d.footer_telefono}` : "", d?.footer_sito].filter(Boolean);
      setFooterLine(parts.join("  ·  "));
      return d;
    },
  });

  const salvaPredefiniti = async () => {
    setSavingDefault(true);
    try {
      await api.put("/impostazioni/email", { email_from_name: fromName.trim(), email_heading: heading.trim() });
      toast.success("Mittente e intestazione salvati come predefiniti");
    } catch (e) { toast.error(e.response?.data?.detail || "Impossibile salvare i predefiniti"); }
    setSavingDefault(false);
  };

  const logoUrl = (companyLogo && user?.company_id) ? `${process.env.REACT_APP_BACKEND_URL}/api/public/company-logo/${user.company_id}` : "";

  const { data: dest = { count: 0, destinatari: [] } } = useQuery({
    queryKey: ["email-dest", target, selectedStructure],
    queryFn: async () => (await api.get("/email-massiva/destinatari", { params: { target, structure_id: selectedStructure } })).data,
  });

  const { data: bozze = [] } = useQuery({
    queryKey: ["bozze-comunicazioni"],
    queryFn: async () => (await api.get("/bozze_comunicazioni")).data,
  });

  const applyTemplate = (t) => { setSubject(TEMPLATES[t].subject); setBody(TEMPLATES[t].body); setLoadedDraftId(""); toast.success("Modello inserito"); };

  const salvaBozza = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Compila oggetto e testo prima di salvare"); return; }
    setSavingDraft(true);
    try {
      const payload = { titolo: subject.trim(), target, subject: subject.trim(), body: body.trim(), heading: heading.trim(), from_name: fromName.trim() };
      if (loadedDraftId) {
        await api.put(`/bozze_comunicazioni/${loadedDraftId}`, payload);
        toast.success("Bozza aggiornata");
      } else {
        const saved = (await api.post("/bozze_comunicazioni", payload)).data;
        setLoadedDraftId(saved.id || "");
        toast.success("Bozza salvata");
      }
      qc.invalidateQueries({ queryKey: ["bozze-comunicazioni"] });
    } catch { toast.error("Errore nel salvataggio della bozza"); }
    setSavingDraft(false);
  };

  const caricaBozza = (id) => {
    const b = bozze.find((x) => x.id === id);
    if (!b) return;
    setTarget(b.target || "ospiti");
    setSubject(b.subject || "");
    setBody(b.body || "");
    if (b.heading !== undefined) setHeading(b.heading || "");
    if (b.from_name) setFromName(b.from_name);
    setLoadedDraftId(b.id);
    toast.success("Bozza caricata");
  };

  const eliminaBozza = async () => {
    if (!loadedDraftId) return;
    try {
      await api.delete(`/bozze_comunicazioni/${loadedDraftId}`);
      setLoadedDraftId("");
      qc.invalidateQueries({ queryKey: ["bozze-comunicazioni"] });
      toast.success("Bozza eliminata");
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const scaricaPdf = () => {
    if (!subject.trim() || !body.trim()) { toast.error("Compila oggetto e testo prima di scaricare"); return; }
    try { exportComunicazione({ subject: subject.trim(), body: body.trim(), logo: companyLogo, heading: heading.trim(), fromName: fromName.trim(), footer: footerLine }); toast.success("PDF generato"); }
    catch { toast.error("Errore nella generazione del PDF"); }
  };
  const condividiWhatsapp = () => {
    if (!subject.trim() || !body.trim()) { toast.error("Compila oggetto e testo prima di condividere"); return; }
    const msg = `*${subject.trim()}*\n\n${body.trim()}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const invia = async (isTest) => {
    if (!subject.trim() || !body.trim()) { toast.error("Compila oggetto e testo"); return; }
    setSending(true);
    try {
      const payload = { target, subject: subject.trim(), html: wrapHtml(subject.trim(), body.trim(), logoUrl, heading.trim(), footerLine), structure_id: selectedStructure, from_name: fromName.trim() };
      if (isTest) payload.test_email = testEmail.trim();
      const res = (await api.post("/email-massiva", payload)).data;
      if (isTest) toast.success("Email di prova inviata");
      else toast.success(`Inviate ${res.inviate} email${res.fallite ? `, ${res.fallite} non riuscite` : ""}`);
      setConfirmOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    setSending(false);
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-5">
        <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Mail size={22} /> Email massiva</h1>
        <p className="text-sm text-muted-foreground">Invia una comunicazione a tutti gli ospiti (familiari), agli operatori o ai fornitori che hanno un'email registrata.</p>
      </div>

      <div className="gr-card p-4 mb-4">
        <Label className="mb-2 block">Destinatari</Label>
        <div className="flex gap-2 mb-3">
          <Button variant={target === "ospiti" ? "default" : "outline"} onClick={() => setTarget("ospiti")} data-testid="mail-target-ospiti"><HeartHandshake size={16} className="mr-1" /> Ospiti / Famiglie</Button>
          <Button variant={target === "operatori" ? "default" : "outline"} onClick={() => setTarget("operatori")} data-testid="mail-target-operatori"><Users size={16} className="mr-1" /> Operatori</Button>
          <Button variant={target === "fornitori" ? "default" : "outline"} onClick={() => setTarget("fornitori")} data-testid="mail-target-fornitori"><Truck size={16} className="mr-1" /> Fornitori</Button>
        </div>
        <div className="text-sm">
          <span className="font-semibold text-primary" data-testid="mail-dest-count">{dest.count}</span> destinatari con email
          {dest.count > 0 && <button onClick={() => setShowList((s) => !s)} className="ml-2 text-xs text-muted-foreground underline inline-flex items-center gap-1" data-testid="mail-toggle-list">vedi elenco <ChevronDown size={12} /></button>}
        </div>
        {showList && (
          <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border bg-secondary/20 p-2 text-xs space-y-1" data-testid="mail-dest-list">
            {dest.destinatari.map((d, i) => <div key={i} className="flex justify-between"><span>{d.nome}</span><span className="text-muted-foreground">{d.email}</span></div>)}
          </div>
        )}
        {dest.count === 0 && <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><AlertTriangle size={13} /> Nessun {target === "ospiti" ? "ospite" : target === "operatori" ? "operatore" : "fornitore"} ha un'email. {target === "fornitori" ? "Aggiungila in Numeri utili (categoria Fornitori)." : "Aggiungila nella scheda anagrafica."}</p>}
      </div>

      {bozze.length > 0 && (
        <div className="gr-card p-4 mb-4" data-testid="mail-bozze-card">
          <Label className="mb-2 block flex items-center gap-1"><FolderOpen size={15} className="text-primary" /> Bozze salvate</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={loadedDraftId || undefined} onValueChange={caricaBozza}>
              <SelectTrigger className="flex-1" data-testid="mail-bozze-select"><SelectValue placeholder="Riapri una comunicazione salvata…" /></SelectTrigger>
              <SelectContent>
                {bozze.map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`mail-bozza-${b.id}`}>
                    {b.titolo || b.subject || "(senza oggetto)"} · {b.target === "operatori" ? "Operatori" : b.target === "fornitori" ? "Fornitori" : "Famiglie"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {loadedDraftId && <Button variant="outline" className="text-destructive" onClick={eliminaBozza} data-testid="mail-bozza-delete"><Trash2 size={15} className="mr-1" /> Elimina</Button>}
          </div>
          {loadedDraftId && <p className="text-xs text-muted-foreground mt-2">Stai modificando una bozza salvata: «Aggiorna bozza» sovrascrive quella esistente.</p>}
        </div>
      )}

      <div className="gr-card p-4 mb-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <Label>Messaggio</Label>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => applyTemplate("ospiti")} data-testid="mail-tpl-ospiti"><FileText size={14} className="mr-1" /> Modello famiglie</Button>
            <Button size="sm" variant="outline" onClick={() => applyTemplate("operatori")} data-testid="mail-tpl-operatori"><FileText size={14} className="mr-1" /> Modello operatori</Button>
            <Button size="sm" variant="outline" onClick={() => applyTemplate("fornitori")} data-testid="mail-tpl-fornitori"><FileText size={14} className="mr-1" /> Modello fornitori</Button>
          </div>
        </div>
        <Label className="text-xs text-muted-foreground">Oggetto</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto dell'email" className="mb-3" data-testid="mail-subject" />
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-xs text-muted-foreground">Nome mittente (da chi arriva l'email)</Label>
            <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Es. Casa di Riposo Orto della Longevità" data-testid="mail-from-name" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Intestazione in alto (facoltativa)</Label>
            <Input value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="Testo sopra l'oggetto (lascia vuoto per usare solo il logo)" data-testid="mail-heading" />
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3 -mt-1">
          <Button size="sm" variant="ghost" className="text-primary h-7 px-2" onClick={salvaPredefiniti} disabled={savingDefault} data-testid="mail-save-default"><Save size={13} className="mr-1" /> Salva mittente e intestazione come predefiniti</Button>
        </div>
        <Label className="text-xs text-muted-foreground">Testo</Label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} placeholder="Scrivi il messaggio…" data-testid="mail-body" />
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
          <span className="text-xs text-muted-foreground w-full sm:w-auto sm:mr-1 self-center">Salva o condividi:</span>
          <Button size="sm" variant="outline" onClick={() => { if (!subject.trim() || !body.trim()) { toast.error("Compila oggetto e testo"); return; } setPreviewOpen(true); }} data-testid="mail-preview"><Eye size={14} className="mr-1" /> Anteprima</Button>
          <Button size="sm" variant="outline" onClick={salvaBozza} disabled={savingDraft} data-testid="mail-save-draft"><Save size={14} className="mr-1" /> {loadedDraftId ? "Aggiorna bozza" : "Salva bozza"}</Button>
          <Button size="sm" variant="outline" onClick={scaricaPdf} data-testid="mail-download-pdf"><FileDown size={14} className="mr-1" /> Scarica PDF</Button>
          <Button size="sm" variant="outline" onClick={condividiWhatsapp} data-testid="mail-share-whatsapp"><Share2 size={14} className="mr-1" /> WhatsApp</Button>
        </div>
      </div>

      <div className="gr-card p-4 flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground">Prova l'invio su un indirizzo (facoltativo)</Label>
          <div className="flex gap-2">
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="tua@email.it" data-testid="mail-test-email" />
            <Button variant="outline" onClick={() => invia(true)} disabled={sending || !testEmail.trim()} data-testid="mail-send-test">Invia prova</Button>
          </div>
        </div>
        <Button className="font-semibold" disabled={sending || dest.count === 0} onClick={() => setConfirmOpen(true)} data-testid="mail-send-all"><Send size={16} className="mr-1" /> Invia a tutti ({dest.count})</Button>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="mail-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye size={18} /> Anteprima email</DialogTitle>
            <DialogDescription>Ecco come apparirà il messaggio al destinatario{logoUrl ? " (con il logo aziendale)" : ""}.</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-secondary/40 p-3 sm:p-5 max-h-[65vh] overflow-y-auto">
            <div className="text-xs text-muted-foreground mb-2 px-1"><span className="font-semibold">Da:</span> {fromName.trim() || "Gestione Residenze"} &nbsp;·&nbsp; <span className="font-semibold">Oggetto:</span> {subject || "(nessun oggetto)"}</div>
            <div dangerouslySetInnerHTML={{ __html: wrapHtml(subject.trim(), body.trim(), logoUrl, heading.trim()) }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="mail-preview-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="mail-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confermi l'invio?</DialogTitle>
            <DialogDescription>Stai per inviare questa email a <b>{dest.count}</b> {target === "ospiti" ? "ospiti / famiglie" : target === "operatori" ? "operatori" : "fornitori"}. L'operazione non è annullabile.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="mail-confirm-cancel">Annulla</Button>
            <Button onClick={() => invia(false)} disabled={sending} data-testid="mail-confirm-send"><Send size={15} className="mr-1" /> {sending ? "Invio…" : "Invia ora"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
